import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import store from "@/store/index";
import createAuth0Client, { Auth0Client } from "@auth0/auth0-spa-js";
import LocalDataService from "@/service/LocalDataService";

export interface Auth0State {
  auth0Client: Auth0Client | null;
  accessToken: string | null;
}

@Module({ dynamic: true, store, name: "auth0", namespaced: true })
class Auth0Module extends VuexModule implements Auth0State {
  public auth0Client: Auth0Client | null = null;
  public accessToken: string | null = null;

  @Action
  public async initClient() {
    const domain = process.env.VUE_APP_AUTH0_DOMAIN;
    const clientId = process.env.VUE_APP_AUTH0_CLIENT_ID;

    if (domain && clientId) {
      try {
        const client = await createAuth0Client({
          domain: domain,
          client_id: clientId,
          redirect_uri: window.location.origin + "/signin",
          useRefreshTokens: true,
          cacheLocation: 'localstorage'
        });

        this.setClient(client);

        const isAuthenticated = await client.isAuthenticated();
        if (isAuthenticated) {
          const claims = await client.getIdTokenClaims();
          if (claims) {
            this.setAccessToken(claims.__raw);
          }
        }
      } catch (e: any) {
        console.error("Auth0 Init Error:", e);
      }
    }
  }

  @Action
  public async handleAuth0() {
    if (this.auth0Client) {
      try {
        await this.auth0Client.handleRedirectCallback();
        
        const claims = await this.auth0Client.getIdTokenClaims();
        if (claims) {
          this.setAccessToken(claims.__raw);
        }
      } catch (e) {
        console.error("Handle Redirect Error:", e);
        throw e;
      }
    }
  }

  @Mutation
  public setClient(client: Auth0Client) {
    this.auth0Client = client;
  }

  @Mutation
  public setAccessToken(token: string) {
    this.accessToken = token;
    LocalDataService.setAccessToken("Bearer " + token);
  }

  @Action
  public signIn() {
    if (this.auth0Client) {
      this.auth0Client.loginWithRedirect({
        redirect_uri: window.location.origin + "/signin",
      });
    }
  }

  @Action
  public logout() {
    if (this.auth0Client) {
      LocalDataService.clearAll();
      this.auth0Client.logout({
        returnTo: window.location.origin + "/signin",
      });
    }
  }
}

export const auth0Module = getModule(Auth0Module);